/**
 * <CommandPalette /> — Cmd-K / Ctrl-K command palette.
 * Phase 13 U5 #297.
 *
 * Global keyboard shortcut opens a fullscreen modal (mobile) or centred
 * modal (desktop) with a fuzzy search over the realtime command catalog
 * defined in AgentControlPanel.tsx. Arrow keys to navigate, Enter to
 * execute. Sensitive commands route through window.confirm before
 * sending the SwarmCommand.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Command, Loader2, X } from "lucide-react";
import { useAgentSocketContext } from "../hooks/useAgentSocketContext";
import { COMMAND_CATALOG } from "./AgentControlPanel";

interface FlatCommand {
  type: string;
  label: string;
  description?: string;
  sensitive?: boolean;
  fields?: Array<{ name: string; placeholder: string }>;
  agentSlug: string;
}

const FLAT: FlatCommand[] = COMMAND_CATALOG.flatMap((p) =>
  p.commands.map((c) => ({ ...c, agentSlug: p.agentSlug })),
);

function fuzzy(query: string, command: FlatCommand): number {
  const haystack = `${command.agentSlug} ${command.label} ${command.type} ${command.description ?? ""}`.toLowerCase();
  const needle = query.toLowerCase();
  if (!needle) return 1;
  // Loose contiguous-substring scoring : exact substring scores high,
  // each missing char drops the score.
  let score = 0;
  let i = 0;
  for (const ch of needle) {
    const idx = haystack.indexOf(ch, i);
    if (idx === -1) return 0;
    score += 1 / (idx - i + 1);
    i = idx + 1;
  }
  return score;
}

export function CommandPalette() {
  const { sendCommand, state: wsState } = useAgentSocketContext();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const matches = useMemo(() => {
    return FLAT
      .map((c) => ({ c, score: fuzzy(query, c) }))
      .filter((m) => m.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .map((m) => m.c);
  }, [query]);

  // Global Cmd-K / Ctrl-K shortcut.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((s) => !s);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Reset selection on query change, focus input on open.
  useEffect(() => {
    setSelectedIdx(0);
  }, [query]);

  useEffect(() => {
    if (open) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      setTimeout(() => inputRef.current?.focus(), 30);
      return () => { document.body.style.overflow = prevOverflow; };
    }
  }, [open]);

  const execute = async (c: FlatCommand) => {
    let payload: Record<string, unknown> | undefined;
    if (c.fields && c.fields.length > 0) {
      payload = {};
      for (const f of c.fields) {
        const v = window.prompt(f.placeholder);
        if (v === null) return;
        payload[f.name] = v;
      }
    }
    if (c.sensitive) {
      const ok = window.confirm(`Confirmer "${c.label}" ?`);
      if (!ok) return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const result = await sendCommand(c.type, payload);
      setLastResult(`✓ ${c.type} · ${JSON.stringify(result).slice(0, 160)}`);
    } catch (err) {
      setLastResult(`✗ ${c.type} · ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const onListKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(matches.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const c = matches[selectedIdx];
      if (c) void execute(c);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-0 sm:p-8"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="w-full sm:w-[640px] sm:max-w-[90vw] sm:rounded-xl bg-[var(--color-bg-elev)] border border-[var(--color-border)] overflow-hidden flex flex-col h-screen sm:h-auto sm:max-h-[80vh]">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--color-border)]">
          <Command className="size-4 text-[var(--color-accent)]" strokeWidth={1.5} aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onListKey}
            placeholder="Tape une commande… (force heartbeat, approve…)"
            className="flex-1 bg-transparent text-base outline-none text-[var(--color-text)] placeholder:text-[var(--color-text-dim)]"
            aria-label="Command search"
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="size-9 grid place-items-center rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/5 transition-colors"
          >
            <X className="size-4" strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>
        <ul className="flex-1 overflow-y-auto divide-y divide-[var(--color-border)]/60" onKeyDown={onListKey}>
          {matches.length === 0 && (
            <li className="px-4 py-6 text-sm text-center text-[var(--color-text-dim)]">
              Aucune commande trouvée
            </li>
          )}
          {matches.map((c, i) => {
            const selected = i === selectedIdx;
            return (
              <li key={c.type}>
                <button
                  type="button"
                  onClick={() => void execute(c)}
                  className={`w-full text-left px-4 py-3 transition-colors ${selected ? "bg-[var(--color-accent-soft)]" : "hover:bg-white/[0.02]"}`}
                  onMouseEnter={() => setSelectedIdx(i)}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className={`font-medium text-sm ${selected ? "text-[var(--color-accent-glow)]" : "text-[var(--color-text)]"}`}>
                      {c.label}
                    </span>
                    <span className="font-mono text-[10px] text-[var(--color-text-dim)] uppercase tracking-wider">
                      {c.agentSlug}
                    </span>
                  </div>
                  <p className="font-mono text-[11px] text-[var(--color-text-muted)] mt-0.5 truncate">
                    {c.type}
                  </p>
                </button>
              </li>
            );
          })}
        </ul>
        <div className="px-4 py-2 border-t border-[var(--color-border)] flex items-center gap-2 text-[10px] font-mono text-[var(--color-text-dim)]">
          {busy ? (
            <>
              <Loader2 className="size-3 animate-spin" strokeWidth={2} aria-hidden="true" />
              <span>executing…</span>
            </>
          ) : lastResult ? (
            <span className="truncate">{lastResult}</span>
          ) : (
            <span>↑↓ navigate · Enter run · Esc close · WS {wsState}</span>
          )}
        </div>
      </div>
    </div>
  );
}
